import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, Eye, Calendar as CalendarIcon, Sparkles, Award, Shield } from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProjects, useDeleteProject } from "@/hooks/api/use-projects";
import { useAuth } from "@/contexts/AuthContext";
import ReadOnlyWrapper from "@/components/ReadOnlyWrapper";
import { cn } from "@/lib/utils";
import { ProtectedImage } from "@/hooks/use-protected-image";

const ClientPortfolio = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.type === 'admin';
  
  const { data, isLoading, error } = useProjects(undefined, { 
    enabled: !isAdmin && !!user 
  });
  const deleteProject = useDeleteProject();
  
  const projects = Array.isArray(data?.data) ? data.data : [];

  const handleDeleteProject = (id: number) => {
    if (confirm(t('portfolio.confirmDelete') || 'هل أنت متأكد من حذف هذا المشروع؟')) {
      deleteProject.mutate(id);
    }
  };

  // Admin doesn't need portfolio
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-2xl mb-6 border border-slate-200">
              <Shield size={40} className="text-slate-400" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              {t('portfolio.adminNotAvailable') || 'Portfolio is only available for clients'}
            </h3>
            <p className="text-slate-600 mb-8">
              {t('portfolio.adminMessage') || 'Administrators do not have a portfolio section. Please navigate to the admin dashboard.'}
            </p>
            <Link 
              to="/admin/dashboard"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('portfolio.goToAdmin') || 'Go to Admin Dashboard'}
              <Shield size={18} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10">
        {/* Enhanced Header */}
        <div className="bg-white/50 backdrop-blur-sm border-b border-slate-200">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Award size={24} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                      {t('portfolio.clientPortfolio') || 'معرض أعمالي'}
                    </h1>
                    <p className="text-slate-600 mt-1">
                      {t('portfolio.addProject') || 'أضف مشاريعك لتعرضها على عملائك'}
                    </p>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Award size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{projects.length}</p>
                      <p className="text-xs text-slate-600">{t('portfolio.totalProjects') || 'إجمالي المشاريع'}</p>
                    </div>
                  </div>
                  
                                  </div>
              </div>
              
              <ReadOnlyWrapper tooltip={t('readOnly.disabled') || 'This action is disabled'}>
                <Button 
                  className="gap-2 bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl" 
                  onClick={() => navigate('/dashboard/portfolio/new')}
                >
                  <Plus size={18} />
                  {t('portfolio.addNew') || 'إضافة مشروع جديد'}
                </Button>
              </ReadOnlyWrapper>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="container mx-auto px-4 py-8">
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <Skeleton className="h-48" />
                  <div className="p-6">
                    <Skeleton className="h-6 w-3/4 mb-4" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3 mb-4" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <Shield size={32} className="text-red-600" />
              </div>
              <p className="text-slate-600 text-lg mb-4">
                {t('common.error') || 'حدث خطأ أثناء تحميل المشاريع'}
              </p>
              <button 
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t('common.retry') || 'إعادة المحاولة'}
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-full mb-6">
                <Award size={40} className="text-slate-400" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                {t('portfolio.noProjects') || 'لا توجد مشاريع'}
              </h3>
              <p className="text-slate-600 mb-8 max-w-md mx-auto">
                {t('portfolio.noProjectsDesc') || 'ابدأ بإضافة أول مشروع لعرضه على عملائك'}
              </p>
              <ReadOnlyWrapper tooltip={t('readOnly.disabled') || 'This action is disabled'}>
                <Button 
                  className="gap-2 bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                  onClick={() => navigate('/dashboard/portfolio/new')}
                >
                  <Plus size={18} />
                  {t('portfolio.addFirstProject') || 'إضافة أول مشروع'}
                </Button>
              </ReadOnlyWrapper>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => {
                const title = typeof project.title === 'string' 
                  ? project.title 
                  : project.title[language] || project.title.en;
                const description = project.description 
                  ? (typeof project.description === 'string' 
                    ? project.description 
                    : project.description[language] || project.description.en)
                  : '';
                const location = project.location
                  ? (typeof project.location === 'string'
                    ? project.location
                    : project.location[language] || project.location.en)
                  : '';
                
                const completedDate = project.completed_date 
                  ? format(new Date(project.completed_date), "PPP", { locale: isRTL ? ar : enUS })
                  : '';
                
                return (
                  <Card key={project.id} className="bg-white border-slate-200 overflow-hidden group hover:shadow-2xl transition-all duration-500 rounded-2xl">
                    {/* Image Gallery */}
                    <div className="h-48 bg-slate-50 relative overflow-hidden">
                      {project.images && project.images.length > 0 ? (
                        <div className="flex h-full">
                          {project.images.slice(0, 2).map((image, index) => (
                            <div
                              key={image.id}
                              className={cn(
                                "flex-1 relative",
                                project.images!.length === 1 ? "w-full" : index === 0 ? "mr-1" : ""
                              )}
                            >
                              <ProtectedImage
                                src={image.url}
                                alt={`${title} - Image ${index + 1}`}
                                containerClassName="w-full h-full"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                              {project.images!.length > 2 && index === 1 && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    +{project.images!.length - 2}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                          <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-primary font-bold text-2xl">{title.charAt(0)}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Hover Actions */}
                      <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <ReadOnlyWrapper tooltip={t('readOnly.disabled') || 'This action is disabled'}>
                          <Button 
                            variant="secondary" 
                            size="icon" 
                            className="h-9 w-9 bg-white/90 hover:bg-white text-slate-700"
                            onClick={() => navigate(`/dashboard/portfolio/${project.id}/edit`)}
                          >
                            <Edit size={16} />
                          </Button>
                        </ReadOnlyWrapper>
                        <ReadOnlyWrapper tooltip={t('readOnly.disabled') || 'This action is disabled'}>
                          <Button 
                            variant="secondary" 
                            size="icon" 
                            className="h-9 w-9 bg-white/90 hover:bg-white text-red-600"
                            onClick={() => handleDeleteProject(project.id)}
                            disabled={deleteProject.isPending}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </ReadOnlyWrapper>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <CardContent className="p-6">
                      <CardHeader className="p-0 pb-4">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-xl text-slate-900 group-hover:text-primary transition-colors duration-300">
                            {title}
                          </CardTitle>
                                                  </div>
                      </CardHeader>
                      
                      {description && (
                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{description}</p>
                      )}
                      
                      {(location || completedDate) && (
                        <div className="space-y-2 mb-4">
                          {location && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <span>📍</span>
                              <span>{location}</span>
                            </p>
                          )}
                          {completedDate && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <CalendarIcon size={12} />
                              <span>{completedDate}</span>
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div className="pt-4 border-t border-slate-100">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-primary hover:text-primary transition-all duration-300"
                          asChild
                        >
                          <Link to={`/dashboard/portfolio/${project.id}`}>
                            <Eye size={16} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {t('portfolio.viewDetails') || 'عرض التفاصيل'}
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientPortfolio;
